/**
 * Parser for URL-driven filter DSL.
 * Converts URL search params to structured FilterSet.
 *
 * DSL format: ?filters=facet:op(values)&facet2:op(values)
 * - Ampersand (&) separates clauses (AND logic between facets)
 * - Colon (:) separates facet from operator
 * - Parentheses wrap values (required for multiple, optional for single)
 * - Comma (,) separates values (OR logic within a facet)
 *
 * Examples:
 * - ?filters=customer:is(maersk)
 * - ?filters=customer:is(maersk)&status:is(active,paused)&internal:not(true)
 */

import { FilterSet, FilterSetSchema, type FilterClause } from './types';

/**
 * Parses the "filters" query parameter from URLSearchParams into a FilterSet.
 *
 * @param searchParams - URLSearchParams from React Router or window.location
 * @returns Parsed and validated FilterSet (empty array if no filters)
 * @throws ZodError if the DSL is malformed
 */
export function parseFilters(searchParams: URLSearchParams): FilterSet {
  const filtersParam = searchParams.get('filters');

  if (!filtersParam || filtersParam.trim() === '') {
    return [];
  }

  // Split by ampersand, but only when not inside parentheses
  // This handles values containing & like "Smith & Co."
  const clauseStrings = splitClauses(filtersParam);
  const clauses: FilterClause[] = [];

  for (const clauseStr of clauseStrings) {
    const clause = parseClause(clauseStr.trim());
    if (clause) {
      clauses.push(clause);
    }
  }

  // Validate with Zod
  return FilterSetSchema.parse(clauses);
}

/**
 * Splits filter string by & but only when outside parentheses
 * Handles values containing & like "customer:is(Smith & Co.)"
 *
 * @param str - The full filter string
 * @returns Array of clause strings
 */
function splitClauses(str: string): string[] {
  const clauses: string[] = [];
  let current = '';
  let depth = 0; // Track parenthesis depth

  for (let i = 0; i < str.length; i++) {
    const char = str[i];

    if (char === '(') {
      depth++;
      current += char;
    } else if (char === ')') {
      depth--;
      current += char;
    } else if (char === '&' && depth === 0) {
      // Only split on & when not inside parentheses
      if (current.trim()) {
        clauses.push(current.trim());
      }
      current = '';
    } else {
      current += char;
    }
  }

  // Add the last clause
  if (current.trim()) {
    clauses.push(current.trim());
  }

  return clauses;
}

/**
 * Parses a single clause string into a FilterClause.
 * Format: facet:op(values) or facet:op(value)
 *
 * @param clauseStr - e.g., "customer:is(maersk)" or "status:not(active,paused)"
 * @returns FilterClause or null if parsing fails
 */
function parseClause(clauseStr: string): FilterClause | null {
  // Match: facet:operator(values)
  // Allow optional parentheses for single values: facet:operator(value) or facet:operator
  const match = clauseStr.match(/^([^:]+):(is|not)(?:\(([^)]*)\))?$/);

  if (!match) {
    return null;
  }

  const [, facet, operator, valuesStr] = match;

  if (!facet || !operator) {
    return null;
  }

  // Parse values: comma-separated
  // Decode DSL delimiter characters that were encoded in the serializer:
  // - Commas (%2C) in values (e.g., "foo,bar" → "foo%2Cbar")
  // - Parentheses (%28, %29) in values
  // URLSearchParams.get() decodes the entire string, but DSL delimiters
  // are explicitly encoded in serializer to prevent parsing ambiguity
  let values: string[] = [];
  if (valuesStr) {
    values = valuesStr
      .split(',')
      .map((v) => {
        const trimmed = v.trim();
        // Safely decode each value - fallback to original if malformed
        try {
          return decodeURIComponent(trimmed);
        } catch {
          // Malformed percent-encoding - use original trimmed value
          return trimmed;
        }
      })
      .filter((v) => v.length > 0);
  }

  // For single value without parentheses: facet:is could be valid if we allow it
  // But per spec, parentheses are required for multi, optional for single
  // If no valuesStr and no parens, treat the whole thing as invalid
  if (values.length === 0 && valuesStr === undefined) {
    // Check if format was facet:op without parens - invalid per our DSL
    return null;
  }

  // Allow empty parentheses: facet:is() - represents a filter with no values
  // This keeps the filter visible in the UI for users to add values

  // Decode facet name (colons are encoded in serializer to prevent confusion with operator delimiter)
  // URLSearchParams.get() already decodes the string, but DSL delimiter characters
  // were explicitly encoded in the serializer, so decode them here
  let decodedFacet: string;
  try {
    decodedFacet = decodeURIComponent(facet.trim());
  } catch {
    // Malformed percent-encoding - use original trimmed value
    console.warn(
      `[parseClause] Failed to decode facet "${facet.trim()}": using as-is`,
    );
    decodedFacet = facet.trim();
  }

  return {
    facet: decodedFacet,
    operator: operator as 'is' | 'not',
    values,
  };
}
